import React, { useState, useEffect } from 'react';
import { formatLondonDate, formatLondonDateTime, londonDateKey } from '../shared/dateUtils';
import './VisitsManagement.css';

interface VisitRecord {
    visitorId: string;
    timestamp: string;
    url?: string;
    referrer?: string;
    userAgent?: string;
    metadata?: any;
}

interface VisitsData {
    date: string;
    visits: VisitRecord[];
    count: number;
}

export const VisitsManagement: React.FC = () => {
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [visitsData, setVisitsData] = useState<VisitsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedVisit, setExpandedVisit] = useState<number | null>(null);

    // Load available dates on mount
    useEffect(() => {
        loadAvailableDates();
    }, []);

    // Load visits when date is selected
    useEffect(() => {
        if (selectedDate) {
            loadVisitsForDate(selectedDate);
        }
    }, [selectedDate]);

    const loadAvailableDates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/visits/dates');
            if (!response.ok) throw new Error('Failed to load dates');
            const data = await response.json() as { dates: string[] };
            setAvailableDates(data.dates || []);

            // Auto-select today's date if available
            const today = formatDateKey(new Date());
            if (data.dates && data.dates.includes(today)) {
                setSelectedDate(today);
            } else if (data.dates && data.dates.length > 0) {
                setSelectedDate(data.dates[0]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load dates');
        } finally {
            setLoading(false);
        }
    };

    const loadVisitsForDate = async (date: string) => {
        setLoading(true);
        setError(null);
        setExpandedVisit(null);
        try {
            const response = await fetch(`/api/visits/${date}`);
            if (!response.ok) throw new Error('Failed to load visits');
            const data = await response.json() as VisitsData;
            setVisitsData(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load visits');
        } finally {
            setLoading(false);
        }
    };

    const formatDateKey = (date: Date): string => {
        // Visits are bucketed by Europe/London calendar day. `londonDateKey`
        // returns `YYYY-MM-DD`; visits API uses `YYYY/MM/DD`.
        return londonDateKey(date).replace(/-/g, '/');
    };

    const formatTimestamp = (timestamp: string): string => {
        return formatLondonDateTime(timestamp);
    };

    const formatDate = (dateStr: string): string => {
        // `dateStr` is `YYYY/MM/DD` (London calendar day). Anchor at noon UTC
        // so the London date is unambiguous, then format in London.
        const [year, month, day] = dateStr.split('/');
        return formatLondonDate(`${year}-${month}-${day}T12:00:00Z`, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        // Could add a toast notification here
    };

    const exportVisits = () => {
        if (!visitsData) return;
        const dataStr = JSON.stringify(visitsData.visits, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `visits-${selectedDate.replace(/\//g, '-')}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="visits-management">
            <div className="visits-header">
                <h1>Visit Tracking</h1>
                <p className="visits-subtitle">View and analyze visitor data stored in R2</p>
            </div>

            {error && (
                <div className="visits-error">
                    <span>⚠️ {error}</span>
                    <button onClick={loadAvailableDates}>Retry</button>
                </div>
            )}

            <div className="visits-controls">
                <div className="date-selector">
                    <label htmlFor="date-select">Select Date:</label>
                    <select
                        id="date-select"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        disabled={loading || availableDates.length === 0}
                    >
                        {availableDates.length === 0 && (
                            <option value="">No data available</option>
                        )}
                        {availableDates.map(date => (
                            <option key={date} value={date}>
                                {formatDate(date)}
                            </option>
                        ))}
                    </select>
                </div>

                {visitsData && visitsData.count > 0 && (
                    <button
                        className="export-btn"
                        onClick={exportVisits}
                        disabled={loading}
                    >
                        📥 Export JSON
                    </button>
                )}
            </div>

            {loading && (
                <div className="visits-loading">
                    <div className="spinner"></div>
                    <p>Loading visits...</p>
                </div>
            )}

            {!loading && visitsData && (
                <div className="visits-content">
                    <div className="visits-stats">
                        <div className="stat-card">
                            <h3>Total Visits</h3>
                            <p className="stat-value">{visitsData.count}</p>
                        </div>
                        <div className="stat-card">
                            <h3>Unique Visitors</h3>
                            <p className="stat-value">
                                {new Set(visitsData.visits.map(v => v.visitorId)).size}
                            </p>
                        </div>
                        <div className="stat-card">
                            <h3>Date</h3>
                            <p className="stat-value">{formatDate(visitsData.date)}</p>
                        </div>
                    </div>

                    {visitsData.count === 0 ? (
                        <div className="no-visits">
                            <p>No visits recorded for this date.</p>
                        </div>
                    ) : (
                        <div className="visits-list">
                            <h2>Visit Details</h2>
                            {visitsData.visits.map((visit, index) => (
                                <div key={index} className="visit-card">
                                    <div className="visit-header" onClick={() => setExpandedVisit(expandedVisit === index ? null : index)}>
                                        <div className="visit-summary">
                                            <span className="visit-time">{formatTimestamp(visit.timestamp)}</span>
                                            <span className="visit-id" onClick={(e) => { e.stopPropagation(); copyToClipboard(visit.visitorId); }}>
                                                👤 {visit.visitorId.slice(0, 8)}...
                                                <span className="copy-hint">📋</span>
                                            </span>
                                        </div>
                                        <span className="expand-icon">{expandedVisit === index ? '▼' : '▶'}</span>
                                    </div>

                                    {expandedVisit === index && (
                                        <div className="visit-details">
                                            <div className="detail-row">
                                                <strong>Visitor ID:</strong>
                                                <span className="monospace">{visit.visitorId}</span>
                                            </div>
                                            {visit.url && (
                                                <div className="detail-row">
                                                    <strong>URL:</strong>
                                                    <a href={visit.url} target="_blank" rel="noopener noreferrer">
                                                        {visit.url}
                                                    </a>
                                                </div>
                                            )}
                                            {visit.referrer && (
                                                <div className="detail-row">
                                                    <strong>Referrer:</strong>
                                                    <span>{visit.referrer}</span>
                                                </div>
                                            )}
                                            {visit.userAgent && (
                                                <div className="detail-row">
                                                    <strong>User Agent:</strong>
                                                    <span className="small-text">{visit.userAgent}</span>
                                                </div>
                                            )}
                                            {visit.metadata && (
                                                <div className="detail-row">
                                                    <strong>Metadata:</strong>
                                                    <pre className="metadata-json">
                                                        {JSON.stringify(visit.metadata, null, 2)}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VisitsManagement;
